import { Telegram, Icon, themeContext, useDebounce, useSharedTranslations } from "shared";
import { IStaffGap, IUserStaff, renderAvatar } from "entities/user/index";
import * as React from 'react';
import { useContext, useEffect, useState } from "react";
import { Divider } from "@yandex-int/hr-components/Divider";
import { Paper, Popper } from '@mui/material';
import { getAbsenceByType } from "../../lib/utils/absenceTypes";
import { toast } from "react-toastify";
import styles from './staff-card.module.scss';

interface IStaffCardProps {
    staffUser?: IUserStaff;
    login: string;
    displayInfo: boolean;
    className?: string;
    popoverClassName?: string;
    valueClassName?: string;
    anchorClassName?: string;
}

export const StaffCard = React.memo(({
                                         staffUser,
                                         login,
                                         className,
                                         popoverClassName,
                                         displayInfo,
                                         valueClassName,
                                         anchorClassName
                                     }: IStaffCardProps) => {
    const t = useSharedTranslations();
    const anchorRef = React.useRef(null);
    const [open, setOpen] = useState<boolean>(false);
    const [hovered, setHovered] = useState<boolean>(false);
    const theme = useContext(themeContext);
    const debounceOpened = useDebounce(open, 500);

    useEffect(() => {
        setOpen((displayInfo || hovered) && !!staffUser);
    }, [displayInfo, hovered, staffUser]);

    const capitalize = (str?: string) => {
        return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
    };

    const onCopyEmailClick = () => {
        const email = `${login}@yandex-team.ru`;
        (async () => {
            await navigator.clipboard.writeText(email);
        })();
        toast.success(t("emailCopied"));
    }

    const renderGapLine = (gap: IStaffGap | null) => {
        if (!gap) {
            return null;
        }
        const absenceType = getAbsenceByType(gap.type);

        return <div className={styles.userGap}>
            {absenceType && <Icon size="m" glyph={absenceType.glyph} className={styles.icon}/>}
            <div className={styles.userGapText}>
                <div className={styles.gapType}>{capitalize(gap.name)}</div>
                {gap.time && <div className={styles.gapTime} dangerouslySetInnerHTML={{ __html: gap.time }}/>}
            </div>
        </div>;
    };

    return (
        <>
            <div
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                className={className}>
                <div className={anchorClassName ? anchorClassName : styles.anchor} ref={anchorRef}></div>
                {renderAvatar(login, theme)}
            </div>
            {staffUser && <Popper
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                open={debounceOpened}
                anchorEl={anchorRef.current}
                className={popoverClassName ? popoverClassName : styles.popper}>
                <Paper
                    className={styles.popoverContainer}
                    elevation={1}
                >
                    <div className={valueClassName ? valueClassName : styles.expandValue}>
                        <div className={styles.avatarSection}>
                            <div className={styles.avatarContainer}>
                                {renderAvatar(login, theme, "m")}
                            </div>
                            <div className={styles.nameSection}>
                                <a
                                    className={styles.nameLink}
                                    target="_blank"
                                    href={`https://staff.yandex-team.ru/${login}`}>{`${staffUser.name.first} ${staffUser.name.last}`}</a>
                                {!staffUser.is_dismissed &&
                                    <div className={styles.email}>{`${login}@yandex-team.ru`}</div>}
                                {staffUser.is_dismissed && staffUser.position &&
                                    <div className={styles.position}>{staffUser.position}</div>}
                            </div>
                        </div>
                        {!staffUser.is_dismissed && <>
                            {staffUser.position && <div className={styles.position}>{staffUser.position}</div>}
                            {renderGapLine(staffUser.gap)}
                            {staffUser.department_name && <div className={styles.groupSection}>
                                <Icon size="m" glyph="group" className={styles.icon}/>
                                {staffUser.department_name}
                            </div>}
                            <Divider/>
                            <div className={styles.buttonGroup}>
                                {staffUser.telegram && staffUser.telegram.length > 0 &&
                                    <a className={styles.telegramBtn}
                                       target="_blank"
                                       href={`https://telegram.me/${staffUser.telegram[0]}`}
                                    >
                                        <Telegram color="white"/>
                                        {t("writeOnTelegram")}
                                    </a>}
                                <a className={styles.mailBtn}
                                onClick={onCopyEmailClick}
                                >
                                    <Icon glyph="mail" size="m"/>
                                    {t("copyEmail")}
                                </a>
                            </div>
                        </>}
                        {staffUser.is_dismissed && <div className={styles.dismissedMessage}>
                            <div className={styles.formerEmployeeTitle}>{t("formerEmployee")}</div>
                            <div dangerouslySetInnerHTML={{ __html: t("formerEmployeeDescription") }}/>
                        </div>}
                    </div>
                </Paper>
            </Popper>}
        </>
    );
});
